/**
 * Module: AppShell
 * Role: Layout route wrapping the 4 tab roots (/, /tasks, /wallet, /profile).
 *       Renders <Outlet/> for the active tab screen + co-located TabBar.
 *       Owns the centralized TG BackButton (tab root → platform.close()).
 *
 * Uses:
 *   - react-router-dom: Outlet, NavLink
 *   - providers/PlatformProvider: usePlatform
 *   - components/atoms: Ic
 *   - styles/tokens: G, P, S, TAB_BAR
 *
 * Used by:
 *   - App:default (pathless layout route)
 *
 * Glossary: ai/glossary/buyer.md
 *
 * DESIGN SOURCE: prototype.jsx:538-584 (TabBar capsule)
 * CONSTRAINT (EC-1, devil C3): TabBar is routing-aware (NavLink/useLocation) →
 *   MUST live here, NOT in components/atoms/ (atom-purity test hard-fails otherwise).
 * CONSTRAINT (EC-12, devil C7): AppShell wraps ONLY the 4 tab roots.
 *   /flow/:slotId, /pantene/*, /debug, /dev/gallery stay siblings — no tab bar on those.
 * CONSTRAINT (EC-13, devil C6, L-042): tab roots hide BackButton so Telegram renders
 *   its native Close control. NEVER navigate(-1) here, NEVER show a Back arrow on a root.
 *   Sub-screens (siblings) own their own Back arrow via usePlatformBack → navigate(-1).
 */
import { CSSProperties, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { usePlatform } from '../providers/PlatformProvider';
import { Ic, type IconName } from '../components/atoms';
import { G, P, S, TAB_BAR } from '../styles/tokens';

// ─── Tab item definitions ─────────────────────────────────────────────────────

interface TabItem {
  to: string;
  label: string;
  icon: IconName;
  /** Required on the index "/" route so Home isn't active on /tasks etc. */
  end?: boolean;
}

const TAB_ITEMS: TabItem[] = [
  { to: '/',        label: 'Находки',  icon: 'home',     end: true },
  { to: '/tasks',   label: 'Задания',  icon: 'tasksTab' },
  { to: '/wallet',  label: 'Копилка',  icon: 'wallet' },
  { to: '/profile', label: 'Я',        icon: 'profile' },
];

// ─── TabBar (co-located, routing-aware) ───────────────────────────────────────
//
// NOT an atom — this component imports from react-router-dom (NavLink).
// Atom-purity rule forbids that inside components/atoms/.
// data-testid="tab-bar" required by EC-12 / HomeScreen DA-11 test selector.

function TabBar() {
  const capsuleStyle: CSSProperties = {
    position: 'absolute',
    bottom: TAB_BAR.bottomOffset,
    left: 12,
    right: 12,
    borderRadius: 28,
    padding: '8px 6px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    zIndex: 30,
    ...G.light,
  };

  return (
    <div data-testid="tab-bar" style={capsuleStyle}>
      {TAB_ITEMS.map(({ to, label, icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          style={{ textDecoration: 'none' }}
        >
          {({ isActive }) => {
            const color = isActive ? P.terracotta : S.secondaryLabel;
            const itemStyle: CSSProperties = {
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              color,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: -0.1,
              fontFamily: 'inherit',
              padding: '4px 12px',
              transition: 'color 0.2s ease',
            };
            return (
              <div style={itemStyle}>
                <Ic name={icon} size={26} color={color} />
                <span>{label}</span>
              </div>
            );
          }}
        </NavLink>
      ))}
    </div>
  );
}

// ─── AppShell layout route ────────────────────────────────────────────────────

export default function AppShell() {
  const platform = usePlatform();

  // Tab roots have nowhere to go "back" to — they ARE the top of the stack.
  // Hiding the BackButton makes Telegram render its native Close control (the
  // user closes the miniapp instead of seeing a back-arrow that secretly closes).
  // Sub-screens outside AppShell (/flow/:slotId, /pantene/*, /product/*) show their
  // own Back arrow via usePlatformBack → navigate(-1).
  // NEVER show a Back arrow here, NEVER navigate(-1) (L-042 / EC-13 / devil C6).
  useEffect(() => {
    platform.hideBackButton();
  }, [platform]);

  const wrapperStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
  };

  return (
    <div style={wrapperStyle}>
      {/* Active tab screen */}
      <Outlet />
      {/* Floating glass TabBar — always on top */}
      <TabBar />
    </div>
  );
}
